import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  InputSignal,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Mapper } from '@shared/helpers/mapper';
import { ExtendedEditorComponent } from '@shared/modules/rich-editor/components/extended-editor/extended-editor.component';
import { DataMode } from '@shared/modules/rich-editor/helpers/enums/data-mode.enum';
import { UnsaveChangesEvaluation } from '@shared/modules/rich-editor/interfaces/unsave-changes-evaluation';
import { RichEditorService } from '@shared/modules/rich-editor/services/rich-editor.service';
import { CurrentUserService } from '@shared/services/current-user.service';
import moment from 'moment';
import { iif } from 'rxjs';
import { Country } from 'src/app/modules/admin/models';
import { CountryService } from 'src/app/modules/admin/services';
import {
  CreateGeneralCountryInformationRequest,
  UpdateGeneralCountryInformationRequest,
} from 'src/app/web-api-client';
import { GeneralInformation } from '../../models/general-information';
import { InformationType } from '../../models/information-type';
import { CountryInformationService } from '../../services/country-information.service';

@Component({
  standalone: false,
  selector: 'gstar-country-information-editor',
  templateUrl: './country-information-editor.component.html',
  styleUrl: './country-information-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountryInformationEditorComponent
  implements OnChanges, OnInit, UnsaveChangesEvaluation, OnDestroy
{
  private readonly countryInfoService = inject(CountryInformationService);
  private readonly activeRichEditor = inject(RichEditorService);
  private readonly countryService = inject(CountryService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentUserService = inject(CurrentUserService);

  countryId: InputSignal<number> = input.required<number>();
  countryInfoTypeId: InputSignal<number> = input.required<number>();

  countryInfo: WritableSignal<GeneralInformation | undefined> =
    signal(undefined);
  country: WritableSignal<Country | undefined> = signal(undefined);
  infoType: WritableSignal<InformationType | undefined> = signal(undefined);
  searchQuery: WritableSignal<string> = signal('');
  mode: WritableSignal<DataMode> = signal(DataMode.readonly);
  reviewDateValue: WritableSignal<Date[]> = signal([]);
  isReviewDateDisabled: WritableSignal<boolean> = signal(true);
  canEditCountry: Signal<boolean> = computed(() =>
    this.currentUserService.canEditCountry(this.countryId())
  );
  showReadOnlyLabel: Signal<boolean> = computed(
    () => this.currentUserService.isCountryAdmin() && !this.canEditCountry()
  );

  reviewDateForm!: FormGroup;

  editor: Signal<ExtendedEditorComponent | undefined> = viewChild(
    ExtendedEditorComponent
  );

  constructor() {
    this.buildForm();

    this.activeRichEditor.richEditor$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        queueMicrotask(() => this.updateHasNotChanges());
      });
  }

  get title(): string {
    return `${this.country()?.name} - ${this.infoType()?.name}`;
  }

  get description(): string {
    return (
      this.countryInfo()?.description ??
      this.translate.instant('countryInformation.noInfoFound', {
        country: this.country()?.name,
      })
    );
  }

  get readonly(): DataMode {
    return DataMode.readonly;
  }

  ngOnInit(): void {
    this.activatedRoute.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        this.searchQuery.set(params.get('q')?.trim() ?? '');
        if (this.mode() === DataMode.readonly) {
          this.activeRichEditor.highlightText(this.searchQuery());
        }
      });

    this.activeRichEditor.onDocumentLoaded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.mode() === DataMode.readonly) {
          this.activeRichEditor.highlightText(this.searchQuery());
        }
      });

    this.reviewDateForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateHasNotChanges();
      });
  }

  hasUnsaveChanges(): boolean {
    return !!(
      this.editor()?.editor() &&
      (!this.editor()?.hasNotChanges() || this.reviewDateForm.dirty)
    );
  }

  saveUnsaveChanges(html: string): void {
    this.save(html);
  }

  isInvalid(): boolean {
    return this.activeRichEditor.isEmpty;
  }

  ngOnChanges(): void {
    if (this.countryId() && this.countryInfoTypeId()) {
      this.loadCountryInformation();
    }
  }

  ngOnDestroy(): void {
    this.activeRichEditor.destroy();
  }

  modeUpdate(mode: DataMode): void {
    if (mode === DataMode.edition && !this.canEditCountry()) {
      this.mode.set(DataMode.readonly);
      this.editor()?.mode.set(DataMode.readonly);
      return;
    }

    if (mode === DataMode.edition) {
      this.searchQuery.set('');
    }

    this.mode.set(mode);
    const isReadonlyMode = mode === DataMode.readonly;

    this.isReviewDateDisabled.set(isReadonlyMode);

    if (isReadonlyMode) {
      this.reviewDateForm.disable({ emitEvent: false });
    } else {
      this.reviewDateForm.enable({ emitEvent: false });
      this.activeRichEditor.setFile(
        this.countryInfo()?.description ?? '',
        this.title
      );
    }

    this.updateHasNotChanges();
  }

  cancelHandler(): void {
    if (this.hasUnsaveChanges()) {
      const modal = this.editor()!.openUnsaveChangeWarningModal(
        this.isInvalid()
      );

      modal.instance
        .afterClosed()
        .subscribe((isConfirm: boolean | undefined) => {
          if (isConfirm) {
            this.activeRichEditor.extractHTML((html: string) =>
              this.save(html)
            );
          } else if (isConfirm !== undefined) {
            this.resetReviewDate();
            this.cancel();
          }
        });
    } else {
      this.resetReviewDate();
      this.cancel();
    }
  }

  save(content: string): void {
    const request:
      | UpdateGeneralCountryInformationRequest
      | CreateGeneralCountryInformationRequest = this.getRequest(content);

    iif(
      () => !!this.countryInfo(),
      this.countryInfoService.update(
        this.countryInfo()?.generalCountryInformationId ?? 0,
        <UpdateGeneralCountryInformationRequest>request
      ),
      this.countryInfoService.create(request)
    ).subscribe((generalInformation: GeneralInformation[]) => {
      if (
        parseInt(this.countryInfoTypeId().toString()) ===
        generalInformation[0].generalCountryInformationTypeId
      ) {
        this.countryInfo.set(generalInformation[0]);
        this.setReviewDate(generalInformation[0].reviewDate);
        this.cancel();
      }
    });
  }

  private cancel(): void {
    this.modeUpdate(DataMode.readonly);
    this.editor()?.mode.set(DataMode.readonly);
  }

  private loadCountryInformation(): void {
    this.countryInfoService
      .getGeneralInformation(this.countryId(), this.countryInfoTypeId())
      .subscribe((generalInformation: GeneralInformation[]) => {
        this.countryInfo.set(generalInformation[0]);
        this.setReviewDate(generalInformation[0]?.reviewDate);
      });

    this.countryInfoService
      .getCountryInformationType(this.countryInfoTypeId())
      .subscribe((info: InformationType) => {
        this.infoType.set(info);
      });

    this.countryService
      .getCountryById(this.countryId())
      .subscribe((country: Country | undefined) => {
        this.country.set(country);
      });
  }

  private getRequest(
    content: string
  ):
    | UpdateGeneralCountryInformationRequest
    | CreateGeneralCountryInformationRequest {
    const target = this.countryInfo()
      ? UpdateGeneralCountryInformationRequest
      : CreateGeneralCountryInformationRequest;
    return Mapper.map(this.countryInfo(), target, [
      {
        description: content,
        countryId: this.countryId(),
        generalCountryInformationId: (
          source: GeneralInformation | undefined
        ): number | undefined =>
          source ? source.generalCountryInformationId : source,
        generalCountryInformationTypeId:
          this.infoType()?.generalCountryInformationTypeId,
        isActive: true,
        actualFrom: (source: GeneralInformation | undefined): Date =>
          source?.actualFrom ?? moment().toDate(),
        reviewDate: this.selectedReviewDate(),
      },
    ]);
  }

  private buildForm(): void {
    this.reviewDateForm = this.formBuilder.group({
      reviewDate: [[]],
    });
    this.reviewDateForm.disable({ emitEvent: false });
  }

  private resetReviewDate(): void {
    this.setReviewDate(this.countryInfo()?.reviewDate);
  }

  private setReviewDate(reviewDate?: Date): void {
    const normalizedDate = this.normalizeReviewDate(reviewDate);
    const dates = normalizedDate ? [normalizedDate, normalizedDate] : [];

    this.reviewDateValue.set(dates);
    this.reviewDateForm.patchValue(
      {
        reviewDate: dates,
      },
      { emitEvent: false }
    );
    this.reviewDateForm.markAsPristine();
    this.reviewDateForm.markAsUntouched();
    this.updateHasNotChanges();
  }

  private selectedReviewDate(): Date | undefined {
    const reviewDate = this.reviewDateForm.get('reviewDate')?.value;

    if (Array.isArray(reviewDate)) {
      return this.normalizeReviewDate(reviewDate[0]);
    }

    return this.normalizeReviewDate(reviewDate ?? undefined);
  }

  private normalizeReviewDate(reviewDate?: Date): Date | undefined {
    if (!reviewDate) {
      return undefined;
    }

    const parsedDate = moment(reviewDate);
    const minimumValidDate = moment('2000-01-01');

    if (!parsedDate.isValid() || parsedDate.isBefore(minimumValidDate)) {
      return undefined;
    }

    return parsedDate.toDate();
  }

  private updateHasNotChanges(): void {
    if (!this.editor()) {
      return;
    }

    const isReadonlyMode = this.mode() === DataMode.readonly;
    const hasDateChanges = this.reviewDateForm.dirty;
    const hasEditorChanges = this.activeRichEditor.hasFileUnsavedChanges;
    const isEditorEmpty = this.activeRichEditor.isEmpty;

    this.editor()?.hasNotChanges.set(
      isReadonlyMode || (!hasDateChanges && !hasEditorChanges) || isEditorEmpty
    );
  }
}
